import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Drawer,
  Group,
  Loader,
  Menu,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowLeft,
  IconCalendarEvent,
  IconCalendarPlus,
  IconCalendarRepeat,
  IconClipboardHeart,
  IconDots,
  IconEdit,
  IconMessage,
  IconMessageCircle2,
  IconQuote,
  IconTrash,
  IconUser,
  IconUsers,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import type { Communication } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePermissions } from '../../../contexts/PermissionContext';
import { RescheduleRequestActions } from '../../client-portal/components/RescheduleRequestActions';
import { RescheduleRequestStatus } from '../../client-portal/components/RescheduleRequestStatus';
import {
  isAppointmentFlowMessage,
  isAppointmentRescheduleMessage,
  parseAppointmentRequestDetails,
  type AppointmentRequestDetailsPayload,
} from '../appointmentRequestMessage';
import {
  getConversationCounterpartyReference,
  readQuotedMessageSnapshot,
} from '../services/messagingFhir';
import { isMessageFromCurrentUser } from '../services/messagingIdentity';
import type { GroupMemberInfo } from '../data/mockMessagingData';
import { useThreadData } from '../hooks/useThreadData';
import { useResolvedCounterpartyDisplay } from '../hooks/useResolvedCounterpartyDisplay';
import { useMessagingActions } from '../hooks/useMessagingActions';
import { notifyUnreadStateChanged } from '../hooks/useUnreadThreadCount';
import { ComposeBar } from './ComposeBar';
import { MessagingParticipantName } from './MessagingParticipantName';
import { MessageAttachmentPreview } from './MessageAttachmentPreview';
import {
  formatDateHeader,
  formatMessageTime,
  groupMessagesByDate,
} from '../messagingTimestamps';
import classes from './ThreadView.module.css';

const isSpaceAdmin = (member: GroupMemberInfo): boolean => {
  return member.extensions?.some(
    (ext) => ext.url === 'https://unify.health/fhir/messaging/space-role' && ext.valueCode === 'admin'
  ) ?? false;
};

/** Within this distance from the bottom, treat the user as "at bottom" for auto-scroll. */
const SCROLL_BOTTOM_THRESHOLD_PX = 64;

function isViewportAtBottom(viewport: HTMLElement): boolean {
  const distanceFromBottom =
    viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
  return distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
}

interface ThreadViewProps {
  threadId: string | null;
  parentMessageId: string | null;
  onBackToMain: () => void;
  onOpenThread: (conversationId: string, parentMessageId: string) => void;
  extraThreads?: Communication[];
  initialMessages?: Communication[];
  /** Real Medplum: refetch thread list so the sidebar shows latest preview/time after send. */
  onAfterMessageSent?: () => void;
  /** Mock mode: push updated thread root into parent so the sidebar preview updates. */

  /** Main thread opened — mark read for sidebar (server + local). */
  onThreadViewed?: (threadId: string) => void;
  /** Mobile: return to the inbox list from the main conversation view. */
  onBackToInbox?: () => void;
  /** Deep-link from task list: scroll to and highlight this message after load. */
  initialScrollToMessageId?: string;
  /** IANA zone for rendering `sent` instants (Practitioner preference, else browser). */
  displayTimeZone: string;
}

export function ThreadView({
  threadId,
  parentMessageId,
  onBackToMain,
  onOpenThread,
  extraThreads,
  initialMessages,
  onAfterMessageSent,
  onThreadViewed,
  onBackToInbox,
  initialScrollToMessageId,
  displayTimeZone,
}: ThreadViewProps): JSX.Element {
  const { isClient: isViewerClient } = usePermissions();
  const scrollRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const prevLoadingRef = useRef(false);
  const prevMessageTotalRef = useRef(0);
  /** True when the user is near the bottom of the message list (updated on scroll). */
  const isAtBottomRef = useRef(true);
  /** Keep following the bottom while attachments/images finish loading. */
  const stickToBottomRef = useRef(false);
  /** After sending, always scroll to show the new message even if ref was stale. */
  const forceScrollOnNextRef = useRef(false);
  const [sending, setSending] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState<Communication | null>(null);

  const {
    thread,
    messages,
    setMessages,
    threadReplies,
    setThreadReplies,
    loading,
    parentMessage,
    groupMembers,
    getReplyCount,
    currentUserRef,
  } = useThreadData(threadId, parentMessageId, { extraThreads, initialMessages });

  const { sendMessage } = useMessagingActions();

  const conversationTitle = useResolvedCounterpartyDisplay(
    thread,
    currentUserRef.reference ?? undefined,
    groupMembers.length > 0 ? groupMembers.map((m) => ({ reference: m.reference, display: m.display })) : undefined,
  );

  const counterpartyReference = useMemo(() => {
    if (!thread) return undefined;
    const memberRefs = groupMembers.map((m) => ({ reference: m.reference, display: m.display }));
    return getConversationCounterpartyReference(
      thread,
      currentUserRef.reference ?? undefined,
      memberRefs.length > 0 ? memberRefs : undefined,
    );
  }, [thread, currentUserRef.reference, groupMembers]);

  const isThreadView = Boolean(threadId && parentMessageId);
  const isArchived = thread?.status === 'completed';

  // Clear quote-reply when entering thread view
  useEffect(() => {
    if (parentMessageId) setReplyingToMessage(null);
  }, [parentMessageId]);

  const threadScrollKey = `${threadId ?? ''}:${parentMessageId ?? ''}`;

  const scrollToMessage = useCallback((messageId: string): void => {
    const viewport = scrollRef.current;
    if (!viewport) return;
    const target = viewport.querySelector(`[data-message-id="${messageId}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add(classes.messageHighlight);
    window.setTimeout(() => target.classList.remove(classes.messageHighlight), 1500);
  }, []);

  const initialScrollAppliedRef = useRef<string | undefined>();

  useEffect(() => {
    initialScrollAppliedRef.current = undefined;
  }, [threadScrollKey]);

  useEffect(() => {
    if (!initialScrollToMessageId || loading) return;
    if (initialScrollAppliedRef.current === initialScrollToMessageId) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToMessage(initialScrollToMessageId);
        initialScrollAppliedRef.current = initialScrollToMessageId;
      });
    });
  }, [initialScrollToMessageId, loading, messages.length, scrollToMessage]);

  useEffect(() => {
    prevLoadingRef.current = true;
    prevMessageTotalRef.current = 0;
    isAtBottomRef.current = true;
    stickToBottomRef.current = false;
    forceScrollOnNextRef.current = false;
  }, [threadScrollKey]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior, stick = true): void => {
    if (stick) {
      stickToBottomRef.current = true;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const viewport = scrollRef.current;
        if (viewport) {
          viewport.scrollTo({ top: viewport.scrollHeight, behavior });
          isAtBottomRef.current = true;
        }
      });
    });
  }, []);

  const handleAttachmentContentLoaded = useCallback(() => {
    if (!stickToBottomRef.current && !isAtBottomRef.current) return;
    scrollToBottom('auto');
  }, [scrollToBottom]);

  // Track whether the user has scrolled away from the bottom.
  useEffect(() => {
    if (loading || !threadId) return;

    const viewport = scrollRef.current;
    if (!viewport) return;

    const updateAtBottom = (): void => {
      const atBottom = isViewportAtBottom(viewport);
      isAtBottomRef.current = atBottom;
      if (!atBottom) {
        stickToBottomRef.current = false;
      }
    };

    viewport.addEventListener('scroll', updateAtBottom, { passive: true });
    updateAtBottom();
    return () => viewport.removeEventListener('scroll', updateAtBottom);
  }, [threadScrollKey, loading, threadId]);

  // Re-scroll when message list height grows (e.g. attachment preview / image load).
  useEffect(() => {
    if (loading || !threadId) return;

    const content = messageListRef.current;
    const viewport = scrollRef.current;
    if (!content || !viewport) return;

    let raf = 0;
    const observer = new ResizeObserver(() => {
      if (!stickToBottomRef.current && !isAtBottomRef.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'auto' });
        isAtBottomRef.current = true;
      });
    });

    observer.observe(content);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [threadScrollKey, loading, threadId]);

  useEffect(() => {
    if (!threadId) return;

    const total = isThreadView
      ? threadReplies.length + (parentMessage ? 1 : 0)
      : messages.length;

    if (loading) {
      prevLoadingRef.current = true;
      return;
    }

    if (prevLoadingRef.current) {
      prevLoadingRef.current = false;
      prevMessageTotalRef.current = total;
      scrollToBottom('auto');
      return;
    }

    if (total > prevMessageTotalRef.current) {
      prevMessageTotalRef.current = total;
      const shouldScroll =
        forceScrollOnNextRef.current || isAtBottomRef.current;
      forceScrollOnNextRef.current = false;
      if (shouldScroll) {
        scrollToBottom('smooth');
      }
    }
  }, [
    loading,
    threadId,
    messages.length,
    threadReplies.length,
    parentMessage,
    isThreadView,
    scrollToBottom,
  ]);

  // Mark thread read when viewing main conversation and when new messages arrive
  // while already viewing. This ensures realtime messages are tracked as read
  // immediately, so the unread counter stays accurate when the user leaves.
  useEffect(() => {
    if (!threadId || parentMessageId || loading) return;
    onThreadViewed?.(threadId);
  }, [threadId, parentMessageId, loading, onThreadViewed, messages.length]);


  const isClinicalChannel =
    thread?.category?.[0]?.coding?.[0]?.code === 'client-conversation' ||
    thread?.category?.[0]?.coding?.[0]?.code === 'client-thread';

  const isGroupConversation =
    thread?.category?.[0]?.coding?.[0]?.code === 'group-thread' ||
    thread?.subject?.reference?.startsWith('Group/') ||
    thread?.subject?.reference?.startsWith('CareTeam/') ||
    false;

  const participants = useMemo(() => {
    if (isGroupConversation) return groupMembers;
    // Real mode: use thread recipients if available
    if (thread?.recipient?.length) {
      return thread.recipient
        .filter((r) => r.reference && r.display)
        .map((r) => ({
          reference: r.reference!,
          display: r.reference === currentUserRef.reference ? 'You' : r.display!,
        }));
    }
    // Mock mode fallback
    if (!thread?.subject) return groupMembers;
    return [
      { reference: currentUserRef.reference ?? 'Practitioner/current-user', display: 'You' },
      { reference: thread.subject.reference ?? '', display: thread.subject.display || 'Unknown' },
    ];
  }, [isGroupConversation, thread, groupMembers, currentUserRef]);

  const [membersOpened, { open: openMembers, close: closeMembers }] = useDisclosure(false);

  const groupedMessages = useMemo(
    () => groupMessagesByDate(messages, displayTimeZone),
    [messages, displayTimeZone],
  );

  // Handle sending a message (main, thread reply, or quote reply)
  const handleSend = async (content: string, attachmentFiles: File[]): Promise<void> => {
    if (!threadId) return;
    setSending(true);
    forceScrollOnNextRef.current = true;

    try {
      const newMessage = await sendMessage({
        threadId,
        content,
        files: attachmentFiles,
        parentMessageId: isThreadView ? parentMessageId : undefined,
        quotedMessage: !isThreadView ? replyingToMessage : undefined,
        threadRecipients: thread?.recipient,
      });

      // Optimistically add only if realtime subscription hasn't already delivered it.
      if (isThreadView) {
        setThreadReplies((prev) => {
          if (prev.some((m) => m.id === newMessage.id)) {
            prevMessageTotalRef.current = prev.length;
            return prev;
          }
          const next = [...prev, newMessage];
          prevMessageTotalRef.current = next.length;
          return next;
        });
      } else {
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMessage.id)) {
            prevMessageTotalRef.current = prev.length;
            return prev;
          }
          const next = [...prev, newMessage];
          prevMessageTotalRef.current = next.length;
          return next;
        });
      }

      // Collapse quote-reply compose chrome before measuring scroll height.
      setReplyingToMessage(null);

      // Always scroll after send: realtime often dedupes the optimistic append so
      // messages.length never changes and the length-based effect would not run.
      forceScrollOnNextRef.current = false;
      scrollToBottom('smooth');

      onAfterMessageSent?.();
      notifyUnreadStateChanged();
    } catch (err) {
      console.error('[Messaging] Failed to send message:', err);
      forceScrollOnNextRef.current = false;
      const message = err instanceof Error ? err.message : 'Failed to send message';
      notifications.show({ color: 'red', title: 'Message not sent', message });
    }

    setSending(false);
  };

  if (!threadId) {
    return (
      <Box className={classes.emptyState}>
        <IconMessage size={48} stroke={1.5} color="var(--mantine-color-unifyGrey-4)" />
        <Text c="dimmed" size="lg">
          Select a conversation to view messages
        </Text>
        <Text c="dimmed" size="sm">
          Choose from the list on the left or start a new conversation
        </Text>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className={classes.emptyState}>
        <Loader size="lg" color="unifyGold" />
        <Text c="dimmed" mt="md">
          Loading messages...
        </Text>
      </Box>
    );
  }

  // ── Thread (sub-thread) view ───────────────────────────────────────────────
  if (isThreadView && threadId && parentMessageId) {
    const threadMessages = parentMessage ? [parentMessage, ...threadReplies] : threadReplies;
    const groupedThreadMessages = groupMessagesByDate(threadMessages, displayTimeZone);

    return (
      <Box className={classes.threadView}>
        <Box className={classes.threadHeader}>
          <Group justify="space-between">
            <Group gap="sm">
              <Tooltip label="Back to conversation">
                <ActionIcon variant="subtle" size="lg" onClick={onBackToMain} aria-label="Back to conversation">
                  <IconArrowLeft size={20} />
                </ActionIcon>
              </Tooltip>
              <Avatar size="md" radius="xl" color="unifyGold">
                {getInitials(conversationTitle)}
              </Avatar>
              <Box>
                <MessagingParticipantName
                  display={conversationTitle}
                  reference={counterpartyReference}
                  isViewerClient={isViewerClient}
                  fw={600}
                  size="md"
                />
                <Text size="xs" c="dimmed">Thread</Text>
              </Box>
            </Group>
            <Group gap="xs">
              <Tooltip label="View members">
                <Button variant="subtle" size="xs" leftSection={<IconUsers size={14} />} onClick={openMembers}>
                  Members
                </Button>
              </Tooltip>
            </Group>
          </Group>
        </Box>

        <ScrollArea viewportRef={scrollRef} style={{ flex: 1 }} className={classes.messageArea}>
          <Box ref={messageListRef} className={classes.messageContainer} p="md">
            <Stack gap="lg">
              {groupedThreadMessages.length === 0 ? (
                <Text c="dimmed" size="sm">No messages yet. Send a message below.</Text>
              ) : (
                groupedThreadMessages.map(({ date, messages: dayMessages }) => (
                  <Box key={date}>
                    <Box className={classes.dateSeparator}>
                      <Text size="xs" c="dimmed" fw={500}>{formatDateHeader(date, displayTimeZone)}</Text>
                    </Box>
                    <Stack gap="sm">
                      {dayMessages.map((msg) => (
                        <MessageBubble
                          key={msg.id}
                          message={msg}
                          replyCount={0}
                          conversationId={null}
                          onAttachmentContentLoaded={handleAttachmentContentLoaded}
                          currentUserRef={currentUserRef.reference ?? ''}
                          displayTimeZone={displayTimeZone}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </Box>
        </ScrollArea>

        {isArchived ? (
          <Box p="md" style={{ textAlign: 'center', borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" c="dimmed">This conversation is archived and read-only.</Text>
          </Box>
        ) : (
          <ComposeBar
            threadId={threadId}
            sending={sending}
            onSend={handleSend}
            isClinicalChannel={isClinicalChannel}
            replyingTo={replyingToMessage}
            onCancelReply={() => setReplyingToMessage(null)}
          />
        )}
        <Drawer title="Members" opened={membersOpened} onClose={closeMembers} position="right" size="sm">
          <MembersList participants={participants} isViewerClient={isViewerClient} />
        </Drawer>
      </Box>
    );
  }

  // ── Main conversation view ─────────────────────────────────────────────────
  return (
    <Box className={classes.threadView}>
      <Box className={classes.threadHeader}>
        <Group justify="space-between">
          <Group gap="sm">
            {onBackToInbox ? (
              <Tooltip label="Back to messages">
                <ActionIcon variant="subtle" size="lg" onClick={onBackToInbox} aria-label="Back to messages">
                  <IconArrowLeft size={20} />
                </ActionIcon>
              </Tooltip>
            ) : null}
            <Avatar size="md" radius="xl" color="unifyGold">
              {getInitials(conversationTitle)}
            </Avatar>
            <Box>
              <MessagingParticipantName
                display={conversationTitle}
                reference={counterpartyReference}
                isViewerClient={isViewerClient}
                fw={600}
                size="md"
              />
              <Text size="xs" c="dimmed">
                {getConversationLabel(thread)} conversation
              </Text>
            </Box>
          </Group>
          <Tooltip label="View members">
            <Button variant="subtle" size="xs" leftSection={<IconUsers size={14} />} onClick={openMembers}>
              Members
            </Button>
          </Tooltip>
        </Group>
      </Box>

      <ScrollArea viewportRef={scrollRef} style={{ flex: 1 }} className={classes.messageArea}>
        <Box ref={messageListRef} className={classes.messageContainer} p="md">
          <Stack gap="lg">
            {groupedMessages.length === 0 ? (
              <Box className={classes.emptyState} style={{ height: 'auto', padding: '40px 0' }}>
                <Text c="dimmed" size="sm">No messages yet. Start the conversation!</Text>
              </Box>
            ) : (
              groupedMessages.map(({ date, messages: dayMessages }) => (
                <Box key={date}>
                  <Box className={classes.dateSeparator}>
                    <Text size="xs" c="dimmed" fw={500}>{formatDateHeader(date, displayTimeZone)}</Text>
                  </Box>
                  <Stack gap="sm">
                    {dayMessages.map((message) => (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        replyCount={getReplyCount(message.id ?? '')}
                        conversationId={threadId}
                        onStartThread={onOpenThread}
                        onViewReplies={onOpenThread}
                        onQuoteReply={setReplyingToMessage}
                        onScrollToQuotedMessage={scrollToMessage}
                        onAttachmentContentLoaded={handleAttachmentContentLoaded}
                        currentUserRef={currentUserRef.reference ?? ''}
                        displayTimeZone={displayTimeZone}
                      />
                    ))}
                  </Stack>
                </Box>
              ))
            )}
          </Stack>
        </Box>
      </ScrollArea>

      {isArchived ? (
        <Box p="md" style={{ textAlign: 'center', borderTop: '1px solid var(--mantine-color-gray-3)' }}>
          <Text size="sm" c="dimmed">This conversation is archived and read-only.</Text>
        </Box>
      ) : (
        <ComposeBar
          threadId={threadId}
          sending={sending}
          onSend={handleSend}
          isClinicalChannel={isClinicalChannel}
          replyingTo={replyingToMessage}
          onCancelReply={() => setReplyingToMessage(null)}
        />
      )}

      <Drawer title="Members" opened={membersOpened} onClose={closeMembers} position="right" size="sm">
        <MembersList participants={participants} isViewerClient={isViewerClient} />
      </Drawer>
    </Box>
  );
}

function MembersList({
  participants,
  isViewerClient,
}: {
  participants: GroupMemberInfo[];
  isViewerClient: boolean;
}): JSX.Element {
  return (
    <Stack gap="sm">
      {participants.length === 0 ? (
        <Text size="sm" c="dimmed">No members loaded.</Text>
      ) : (
        participants.map((member) => (
          <Group key={member.reference} gap="sm" p="xs">
            <Avatar size="sm" radius="xl" color="unifySage">{getInitials(member.display)}</Avatar>
            <Box style={{ flex: 1 }}>
              <MessagingParticipantName
                display={member.display}
                reference={member.reference}
                isViewerClient={isViewerClient}
              />
            </Box>
            {isSpaceAdmin(member) && (
              <Badge size="xs" variant="light" color="gold">Admin</Badge>
            )}
          </Group>
        ))
      )}
    </Stack>
  );
}

// ── Edit / Delete stubs ─────────────────────────────────────────────────────

const handleEditMessage = (messageId: string): void => {
  // TODO: Set editing state, populate compose bar with message content
  void messageId;
};

const handleDeleteMessage = async (messageId: string): Promise<void> => {
  // TODO: PATCH Communication with deletedAt/deletedBy extensions
  void messageId;
};

// ── MessageBubble ────────────────────────────────────────────────────────────

interface MessageBubbleProps {
  message: Communication;
  replyCount: number;
  conversationId: string | null;
  onStartThread?: (conversationId: string, parentMessageId: string) => void;
  onViewReplies?: (conversationId: string, parentMessageId: string) => void;
  onQuoteReply?: (message: Communication) => void;
  onScrollToQuotedMessage?: (messageId: string) => void;
  onAttachmentContentLoaded?: () => void;
  isParentInThreadView?: boolean;
  currentUserRef: string;
  displayTimeZone: string;
}

function getQuotedFromMessage(
  message: Communication,
): { id: string; sender: string; content: string; imageUrl?: string } | null {
  return readQuotedMessageSnapshot(message);
}

function AppointmentDetailRow({
  icon: Icon,
  label,
  value,
  iconAccent,
  multiline = false,
}: {
  icon: typeof IconUser;
  label: string;
  value: string;
  iconAccent: string;
  multiline?: boolean;
}): JSX.Element {
  return (
    <Group gap="xs" align="flex-start" wrap="wrap">
      <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
        <Icon size={15} stroke={1.5} style={{ color: iconAccent, flexShrink: 0, marginTop: 2 }} />
        <Text size="sm" c="dimmed">{label}</Text>
      </Group>
      <Text
        size="sm"
        fw={600}
        style={{
          flex: '1 1 8rem',
          minWidth: 0,
          wordBreak: 'break-word',
          whiteSpace: multiline ? 'pre-wrap' : undefined,
        }}
      >
        {value}
      </Text>
    </Group>
  );
}

function AppointmentRequestMessageBubble({
  message,
  details,
  isOwnMessage,
  displayTimeZone,
}: {
  message: Communication;
  details: AppointmentRequestDetailsPayload;
  isOwnMessage: boolean;
  displayTimeZone: string;
}): JSX.Element {
  const { role } = usePermissions();
  const [respondedDecision, setRespondedDecision] = useState<'accept' | 'deny' | undefined>();
  const isReschedule = isAppointmentRescheduleMessage(message);
  const effectiveDecision = details.providerDecision ?? respondedDecision;
  const showRescheduleProviderControls =
    isReschedule &&
    !isOwnMessage &&
    role !== 'client' &&
    Boolean(details.proposedAppointmentId) &&
    !effectiveDecision;
  const showNewAppointmentProviderControls =
    !isReschedule &&
    !isOwnMessage &&
    role !== 'client' &&
    Boolean(details.proposedAppointmentId) &&
    !effectiveDecision;
  const showProviderActions = showRescheduleProviderControls || showNewAppointmentProviderControls;
  const timestamp = message.sent ? formatMessageTime(message.sent, displayTimeZone) : '';

  const pendingStyle = isReschedule
    ? {
        border: 'var(--mantine-color-violet-3)',
        bg: 'var(--mantine-color-violet-0)',
        shadow: '0 1px 3px rgba(124, 58, 237, 0.1)',
        accent: 'var(--mantine-color-violet-6)',
        title: 'var(--mantine-color-violet-7)',
        badge: 'violet' as const,
      }
    : {
        border: 'var(--mantine-color-blue-3)',
        bg: 'var(--mantine-color-blue-0)',
        shadow: '0 1px 3px rgba(37, 99, 235, 0.08)',
        accent: 'var(--mantine-color-blue-6)',
        title: 'var(--mantine-color-blue-7)',
        badge: 'blue' as const,
      };

  const decisionStyle =
    effectiveDecision === 'accept'
      ? {
          border: 'var(--mantine-color-green-3)',
          bg: 'var(--mantine-color-green-0)',
          shadow: '0 1px 3px rgba(34, 197, 94, 0.12)',
          accent: 'var(--mantine-color-green-6)',
          title: 'var(--mantine-color-green-7)',
          badge: 'green' as const,
        }
      : effectiveDecision === 'deny'
        ? {
            border: 'var(--mantine-color-red-3)',
            bg: 'var(--mantine-color-red-0)',
            shadow: '0 1px 3px rgba(239, 68, 68, 0.12)',
            accent: 'var(--mantine-color-red-6)',
            title: 'var(--mantine-color-red-7)',
            badge: 'red' as const,
          }
        : pendingStyle;

  const paperBorder = decisionStyle.border;
  const paperBg = decisionStyle.bg;
  const paperShadow = decisionStyle.shadow;
  const iconAccent = decisionStyle.accent;
  const iconTitle = decisionStyle.title;
  const badgeColor = decisionStyle.badge;

  return (
    <Box className={classes.messageBubbleWrapper}>
      <Box className={`${classes.messageBubble} ${isOwnMessage ? classes.own : classes.other}`}>
        {!isOwnMessage && (
          <Avatar size="sm" radius="xl" color="unifySage">
            {getInitials(message.sender?.display || 'U')}
          </Avatar>
        )}
        <Box className={classes.bubbleContent} style={{ maxWidth: 'min(440px, 100%)', minWidth: 0, width: '100%' }}>
          {!isOwnMessage && (
            <Text size="xs" fw={500} mb={4} c="dimmed">{message.sender?.display || 'Unknown'}</Text>
          )}
          <Paper
            p="lg"
            radius="md"
            withBorder
            style={{ borderColor: paperBorder, backgroundColor: paperBg, boxShadow: paperShadow, overflow: 'hidden' }}
          >
            <Group gap="xs" mb="md" wrap="wrap">
              {isReschedule ? (
                <IconCalendarRepeat size={16} style={{ color: iconTitle, flexShrink: 0 }} />
              ) : (
                <IconCalendarPlus size={16} style={{ color: iconTitle, flexShrink: 0 }} />
              )}
              <Badge
                variant="light"
                color={badgeColor}
                size="sm"
                style={{ maxWidth: '100%', whiteSpace: 'normal', height: 'auto' }}
              >
                {isReschedule ? 'Reschedule request' : 'Appointment request'}
              </Badge>
            </Group>
            <Stack gap="xs">
              <AppointmentDetailRow
                icon={IconUser}
                label="Provider"
                value={details.providerName}
                iconAccent={iconAccent}
              />
              <AppointmentDetailRow
                icon={IconClipboardHeart}
                label="Episode of care"
                value={details.serviceType}
                iconAccent={iconAccent}
              />
              {details.previousWhen && (
                <AppointmentDetailRow
                  icon={IconCalendarEvent}
                  label="Current time"
                  value={details.previousWhen}
                  iconAccent={iconAccent}
                />
              )}
              <AppointmentDetailRow
                icon={IconCalendarEvent}
                label={isReschedule ? 'Requested time' : 'Preferred time'}
                value={details.preferredWhen}
                iconAccent={iconAccent}
              />
              {details.note && (
                <AppointmentDetailRow
                  icon={IconMessage}
                  label="Note"
                  value={details.note}
                  iconAccent={iconAccent}
                  multiline
                />
              )}
            </Stack>
            {showProviderActions && details.proposedAppointmentId ? (
              <RescheduleRequestActions
                proposedAppointmentId={details.proposedAppointmentId}
                communicationId={message.id}
                originalAppointmentId={details.appointmentId}
                initialDecision={details.providerDecision}
                onCompleted={setRespondedDecision}
              />
            ) : (
              <RescheduleRequestStatus
                decision={effectiveDecision}
                pending={isOwnMessage && !effectiveDecision}
              />
            )}
          </Paper>
          <Group gap="xs" mt={4} justify={isOwnMessage ? 'flex-end' : 'flex-start'} className={classes.messageMetadata}>
            <Text size="xs" c="dimmed">{timestamp}</Text>
          </Group>
        </Box>
      </Box>
    </Box>
  );
}

function MessageBubble({
  message,
  replyCount,
  conversationId,
  onStartThread,
  onViewReplies,
  onQuoteReply,
  onScrollToQuotedMessage,
  onAttachmentContentLoaded,
  isParentInThreadView = false,
  currentUserRef,
  displayTimeZone,
}: MessageBubbleProps): JSX.Element {
  const [hover, setHover] = useState(false);
  const isOwnMessage = isMessageFromCurrentUser(message, currentUserRef);

  const isSystemMessage = message.category?.[0]?.coding?.[0]?.code === 'system';
  const quoted = getQuotedFromMessage(message);
  const showQuoteReply = !isParentInThreadView && onQuoteReply && conversationId;

  if (isSystemMessage) {
    return (
      <Box className={classes.systemMessage}>
        <Text size="xs" c="dimmed">{message.payload?.[0]?.contentString}</Text>
      </Box>
    );
  }

  const apptDetails = isAppointmentFlowMessage(message) ? parseAppointmentRequestDetails(message) : null;
  if (apptDetails) {
    return (
      <AppointmentRequestMessageBubble
        message={message}
        details={apptDetails}
        isOwnMessage={isOwnMessage}
        displayTimeZone={displayTimeZone}
      />
    );
  }

  const deletedAtExt = message.extension?.find((e) => e.url === 'https://unify.health/fhir/messaging/deleted-at');
  const isDeleted = Boolean(deletedAtExt);
  const isEdited =
    message.extension?.find((e) => e.url === 'https://unify.health/fhir/messaging/is-edited')?.valueBoolean === true;

  const content =
    message.payload?.find((p) => p.contentString != null && String(p.contentString).length > 0)?.contentString ?? '';
  const attachments = message.payload?.filter((p) => p.contentAttachment) ?? [];
  const timestamp = message.sent ? formatMessageTime(message.sent, displayTimeZone) : '';
  const receivedTime = message.received ? formatMessageTime(message.received, displayTimeZone) : null;
  const hasReplies = replyCount > 0;
  const showThreadAction =
    !isParentInThreadView &&
    conversationId &&
    message.id &&
    (onStartThread || onViewReplies) &&
    !isAppointmentFlowMessage(message);

  const handleThreadClick = (): void => {
    if (!conversationId || !message.id) return;
    if (hasReplies && onViewReplies) {
      onViewReplies(conversationId, message.id);
    } else if (onStartThread) {
      onStartThread(conversationId, message.id);
    }
  };

  const handleQuotedClick = (): void => {
    if (!quoted?.id || !onScrollToQuotedMessage) return;
    onScrollToQuotedMessage(quoted.id);
  };

  return (
    <Box
      className={classes.messageBubbleWrapper}
      data-message-id={message.id}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Box className={`${classes.messageBubble} ${isOwnMessage ? classes.own : classes.other}`}>
        {!isOwnMessage && (
          <Avatar size="sm" radius="xl" color="unifySage">{getInitials(message.sender?.display || 'U')}</Avatar>
        )}
        <Box className={classes.bubbleContent}>
          {!isOwnMessage && (
            <Text size="xs" fw={500} mb={4} c="dimmed">{message.sender?.display || 'Unknown'}</Text>
          )}
          <Paper className={`${classes.bubble} ${isOwnMessage ? classes.ownBubble : classes.otherBubble}`} p="sm" radius="lg">
            {isDeleted ? (
              <Text size="sm" fs="italic" c="dimmed">
                Message deleted by{' '}
                {message.extension?.find((e) => e.url === 'https://unify.health/fhir/messaging/deleted-by')
                  ?.valueReference?.display ?? 'Unknown'}
              </Text>
            ) : (
              <>
                {quoted && (
                  <Box
                    {...(quoted.id && onScrollToQuotedMessage
                      ? {
                          component: 'button' as const,
                          type: 'button' as const,
                          onClick: handleQuotedClick,
                          'aria-label': `Jump to message from ${quoted.sender}`,
                        }
                      : {})}
                    className={`${classes.quotedBlock}${quoted.id && onScrollToQuotedMessage ? ` ${classes.quotedBlockClickable}` : ''}`}
                  >
                    <Text className={classes.quotedBlockSender}>{quoted.sender}</Text>
                    {quoted.imageUrl ? (
                      <MessageAttachmentPreview
                        attachment={{ url: quoted.imageUrl, contentType: 'image/jpeg', title: quoted.content || 'Image' }}
                        onContentLoaded={onAttachmentContentLoaded}
                      />
                    ) : (
                      <Text className={classes.quotedBlockContent}>{quoted.content}</Text>
                    )}
                  </Box>
                )}
                {content ? <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{content}</Text> : null}
                {attachments.length > 0 && (
                  <Stack gap="xs" mt={content ? 'xs' : 0}>
                    {attachments.map((att, idx) => (
                      <Box key={idx} className={classes.attachmentMedplum}>
                        <MessageAttachmentPreview
                          attachment={att.contentAttachment!}
                          onContentLoaded={onAttachmentContentLoaded}
                        />
                      </Box>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </Paper>
          <Group
            gap="xs"
            mt={4}
            justify={isOwnMessage ? 'flex-end' : 'flex-start'}
            wrap="nowrap"
            className={classes.messageMetadata}
          >
            {!isOwnMessage && (
              <>
                <Text size="xs" c="dimmed" className={classes.messageMetadataTime}>
                  {timestamp}
                </Text>
                {isEdited && <Text size="xs" c="dimmed">(edited)</Text>}
                {receivedTime && (
                  <Text size="xs" c="dimmed" ml={4}>· received {receivedTime}</Text>
                )}
                {showThreadAction && (hover || hasReplies) && (
                  <Tooltip label={hasReplies ? 'View replies' : 'Start thread'}>
                    <Text
                      component="button"
                      size="xs"
                      className={classes.messageActionLink}
                      onClick={handleThreadClick}
                      type="button"
                    >
                      <IconMessageCircle2 size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                      {hasReplies ? `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}` : 'Start thread'}
                    </Text>
                  </Tooltip>
                )}
                {showQuoteReply && hover && (
                  <Tooltip label="Quote reply">
                    <Text
                      component="button"
                      size="xs"
                      className={classes.messageActionLink}
                      onClick={() => onQuoteReply(message)}
                      type="button"
                    >
                      <IconQuote size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                      Reply
                    </Text>
                  </Tooltip>
                )}
              </>
            )}
            {isOwnMessage && (
              <>
                {!isDeleted && hover && message.id && (
                  <Menu shadow="md" width={160} position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="xs" color="gray">
                        <IconDots size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => handleEditMessage(message.id!)}>
                        Edit message
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={() => void handleDeleteMessage(message.id!)}
                      >
                        Delete message
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
                {showThreadAction && (hover || hasReplies) && (
                  <Tooltip label={hasReplies ? 'View replies' : 'Start thread'}>
                    <Text
                      component="button"
                      size="xs"
                      className={classes.messageActionLink}
                      onClick={handleThreadClick}
                      type="button"
                    >
                      <IconMessageCircle2 size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                      {hasReplies ? `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}` : 'Start thread'}
                    </Text>
                  </Tooltip>
                )}
                {showQuoteReply && hover && (
                  <Tooltip label="Quote reply">
                    <Text
                      component="button"
                      size="xs"
                      className={classes.messageActionLink}
                      onClick={() => onQuoteReply(message)}
                      type="button"
                    >
                      <IconQuote size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                      Reply
                    </Text>
                  </Tooltip>
                )}
                <Text size="xs" c="dimmed" className={classes.messageMetadataTime}>
                  {timestamp}
                </Text>
                {isEdited && <Text size="xs" c="dimmed">(edited)</Text>}
                {receivedTime && (
                  <Text size="xs" c="dimmed" ml={4}>· received {receivedTime}</Text>
                )}
              </>
            )}
          </Group>
        </Box>
      </Box>
    </Box>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────



function getInitials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

function getConversationLabel(thread: Communication | undefined): string {
  const code = thread?.category?.[0]?.coding?.[0]?.code;
  if (code === 'client-thread') return 'Client';
  if (code === 'provider-thread') return 'Provider';
  if (code === 'group-thread') return 'Group';
  const ref = thread?.subject?.reference ?? '';
  if (ref.startsWith('Patient/')) return 'Client';
  if (ref.startsWith('Practitioner/')) return 'Provider';
  if (ref.startsWith('Group/') || ref.startsWith('CareTeam/')) return 'Group';
  return 'Conversation';
}
